import { buildShells, advanceShells } from "../arena/shells";
import { advanceAutoLaunch } from "../auto-launch/advance";
import { runEquipCosmeticCommand } from "../cosmetics/commands";
import { advanceCores } from "../cores/physics";
import { runDismissSupplyCrateResultCommand, runOpenSupplyCrateCommand, runPresentSupplyCrateCommand } from "../crates/commands";
import { claimDailyIgnition, consumeDailyIgnition, syncDailyIgnition } from "../daily-ignition";
import { createLuckySparkRuntimeState, runClaimLuckySparkCommand, type LuckySparkRuntimeState } from "../events/lucky-spark";
import { buildOverdriveView, runPrestigeOverdriveCommand } from "../events/overdrive";
import { advanceReactorStorm, type ReactorStormRuntimeState } from "../events/reactor-storm";
import { lookupProfileProgress } from "../progression/levels";
import { loadOrbitForgeSave, writeOrbitForgeSave } from "../save/storage";
import type { OrbitForgeSave } from "../save/types";
import { calculateUpgradeEffects } from "../upgrades/effects";
import type { UpgradeKind } from "../upgrades/definitions";
import { bounceImpactFeedback, overdriveZoneFeedback, polarityMatchFeedback } from "./arena-feedback";
import { runAutoLaunchRuntimeCommand } from "./auto-launch";
import { applyCollectedCoreRewards } from "./collections";
import { runBuyUpgradeCommand, runLaunchCoreCommand, runRollbackUpgradeCommand } from "./commands";
import { createFeedbackEvent, pruneExpiredFeedback, type FeedbackDraft } from "./feedback";
import { advanceRuntimePeriodStats } from "./periods";
import { buildRuntimeSnapshot } from "./snapshot";
import { runStormControl } from "./storm-control";
import { advanceRuntimeTimedEvents } from "./timed-events";
import { rebuildShellsKeepingAngles } from "./upgrade-feedback";
import type { FeedbackEvent, GameCommand, GameSnapshot, GameSubscriber, ShellState } from "./types";

const AUTOSAVE_INTERVAL_SECONDS = 4;

export class OrbitForgeRuntime {
  private save: OrbitForgeSave;
  private readonly loadedFrom: GameSnapshot["loadedFrom"];
  private shells: readonly ShellState[];
  private cores: GameSnapshot["arena"]["cores"] = [];
  private storm: ReactorStormRuntimeState | null = null;
  private luckySpark: LuckySparkRuntimeState = createLuckySparkRuntimeState();
  private feedback: FeedbackEvent[] = [];
  private subscribers: GameSubscriber[] = [];
  private activePanel: string | null = null;
  private stormStopConfirmSeconds = 0;
  private shellBoosting = false;
  private manualCooldownSeconds = 0;
  private elapsedSeconds = 0;
  private autosaveSeconds = 0;
  private lastPayout = 0;
  private feedbackId = 1;
  private dirty = false;

  constructor(storage: Storage | undefined, nowUnixSeconds: number) {
    const result = loadOrbitForgeSave(storage, nowUnixSeconds);
    this.save = result.save;
    this.loadedFrom = result.source;
    this.dirty = syncDailyIgnition(this.save, nowUnixSeconds, this.progress().prestigeLevel);
    this.shells = buildShells(this.effects());
  }

  subscribe(subscriber: GameSubscriber): () => void {
    this.subscribers = [...this.subscribers, subscriber];
    subscriber(this.snapshot());
    return () => {
      this.subscribers = this.subscribers.filter((entry) => entry !== subscriber);
    };
  }

  dispatch(command: GameCommand): void {
    if (command.type === "launch-core") {
      this.launchCore(command.frameX, command.frameY);
    } else if (command.type === "set-shell-boost") {
      this.shellBoosting = command.active;
    } else if (command.type === "open-panel") {
      this.activePanel = this.activePanel === command.panel ? null : command.panel;
      this.emitFeedback({ kind: "pulse", target: command.panel });
    } else if (command.type === "open-crate") {
      this.openCrate(command.crateId);
    } else if (command.type === "present-crate-result") {
      this.presentCrateResult();
    } else if (command.type === "dismiss-crate-result") {
      this.dismissCrateResult();
    } else if (command.type === "claim-daily-ignition") {
      this.claimDailyIgnition();
    } else if (command.type === "claim-lucky-spark") {
      this.claimLuckySpark();
    } else if (command.type === "start-storm") {
      this.startStorm();
    } else if (command.type === "start-prestige-overdrive") {
      this.startPrestigeOverdrive();
    } else if (
      command.type === "buy-auto-ticket" ||
      command.type === "use-auto-ticket" ||
      command.type === "buy-auto-upgrade" ||
      command.type === "toggle-auto-extend"
    ) {
      const result = runAutoLaunchRuntimeCommand(this.save, this.progress(), command);
      this.dirty = this.dirty || result.dirty;
      this.emitFeedbackBatch(result.feedback);
    } else if (command.type === "buy-upgrade") {
      this.buyUpgrade(command.kind);
    } else if (command.type === "rollback-upgrade") {
      this.rollbackUpgrade(command.kind);
    } else if (command.type === "equip-cosmetic") {
      this.equipCosmetic(command.kind, command.id);
    } else if (command.type === "emit-feedback") {
      this.emitFeedback(command.event);
    } else {
      this.activePanel = null;
    }
    this.publish();
  }

  tick(deltaSeconds: number, storage: Storage | undefined): void {
    const cappedDelta = Math.min(deltaSeconds, 0.05);
    this.elapsedSeconds += cappedDelta;
    this.manualCooldownSeconds = Math.max(0, this.manualCooldownSeconds - cappedDelta);
    this.stormStopConfirmSeconds = Math.max(0, this.stormStopConfirmSeconds - cappedDelta);
    this.advancePeriodStats(cappedDelta);
    this.advanceTimedEvents(cappedDelta);
    this.advanceAuto(cappedDelta);
    advanceShells(this.shells, cappedDelta, this.shellBoosting);

    const overdrive = this.overdriveView();
    const physics = advanceCores(this.cores, this.shells, cappedDelta, this.effects(), {
      overdriveActive: overdrive.active,
      overdriveZoneMultiplier: overdrive.zoneMultiplier
    });
    this.applyBounceStats(physics.bounceCoreIds);
    this.emitFeedbackBatch(bounceImpactFeedback(physics.bounceImpacts, this.cores, this.effects()));
    this.emitFeedbackBatch(polarityMatchFeedback(physics.polarityMatches));
    this.emitFeedbackBatch(overdriveZoneFeedback(physics.overdriveZoneHits));
    this.applyCollections(physics.collectedCores);
    this.advanceStorm(cappedDelta);
    this.pruneFeedback();

    this.autosaveSeconds += cappedDelta;
    if (this.dirty && this.autosaveSeconds >= AUTOSAVE_INTERVAL_SECONDS) {
      this.writeSave(storage);
    }

    this.publish();
  }

  flushSave(storage: Storage | undefined): void {
    if (this.dirty) {
      this.writeSave(storage);
    }
  }

  snapshot(): GameSnapshot {
    return buildRuntimeSnapshot({
      save: this.save,
      shells: this.shells,
      cores: this.cores,
      feedback: this.feedback,
      activePanel: this.activePanel,
      activeStorm: this.storm,
      luckySpark: this.luckySpark,
      stormStopPending: this.stormStopConfirmSeconds > 0,
      shellBoosting: this.shellBoosting,
      lastPayout: this.lastPayout,
      manualCooldownSeconds: this.manualCooldownSeconds,
      loadedFrom: this.loadedFrom,
      nowUnixSeconds: Math.floor(Date.now() / 1000)
    });
  }

  private launchCore(frameX: number, frameY: number): void {
    const result = runLaunchCoreCommand({
      save: this.save,
      cores: this.cores,
      effects: this.effects(),
      frameX,
      frameY,
      manualCooldownSeconds: this.manualCooldownSeconds
    });
    this.cores = result.cores;
    this.manualCooldownSeconds = result.manualCooldownSeconds;
    this.dirty = this.dirty || result.dirty;
    this.emitFeedbackBatch(result.feedback);
  }

  private buyUpgrade(kind: UpgradeKind): void {
    const result = runBuyUpgradeCommand(this.save, kind);
    if (result.rebuildShells) {
      this.rebuildShells();
    }
    this.dirty = this.dirty || result.dirty;
    this.emitFeedbackBatch(result.feedback);
  }

  private rollbackUpgrade(kind: UpgradeKind): void {
    const result = runRollbackUpgradeCommand(this.save, kind);
    if (result.rebuildShells) {
      this.rebuildShells();
    }
    this.dirty = this.dirty || result.dirty;
    this.emitFeedbackBatch(result.feedback);
  }

  private equipCosmetic(kind: "shellSkin" | "corePattern", id: string): void {
    const result = runEquipCosmeticCommand(this.save, this.progress(), kind, id);
    this.dirty = this.dirty || result.dirty;
    this.emitFeedbackBatch(result.feedback);
  }

  private claimDailyIgnition(): void {
    this.activePanel = null;
    const claimed = claimDailyIgnition(this.save);
    this.dirty = this.dirty || claimed;
    this.emitFeedback({ kind: claimed ? "pulse" : "denied", target: "daily" });
  }

  private openCrate(crateId: "free" | "mini" | "neon" | "jackpot"): void {
    const result = runOpenSupplyCrateCommand(this.save, crateId, this.progress(), Math.floor(Date.now() / 1000));
    this.dirty = this.dirty || result.dirty;
    this.emitFeedback({
      kind: result.status === "opened" ? "pulse" : "denied",
      target: result.status === "opened" ? "crate-reveal" : "crates"
    });
  }

  private presentCrateResult(): void {
    const result = runPresentSupplyCrateCommand({
      save: this.save,
      cores: this.cores,
      effects: this.effects(),
      nowUnixSeconds: Math.floor(Date.now() / 1000)
    });
    this.cores = result.cores;
    this.dirty = this.dirty || result.dirty;
    this.emitFeedbackBatch(result.feedback);
  }

  private dismissCrateResult(): void {
    const result = runDismissSupplyCrateResultCommand(this.save);
    this.dirty = this.dirty || result.dirty;
    this.emitFeedback({
      kind: result.dirty ? "pulse" : "denied",
      target: "crates"
    });
  }

  private startStorm(): void {
    this.activePanel = null;
    const result = runStormControl({
      save: this.save,
      progress: this.progress(),
      overdrive: this.overdriveView(),
      state: this.storm,
      stopConfirmSeconds: this.stormStopConfirmSeconds
    });
    this.storm = result.state;
    this.stormStopConfirmSeconds = result.stopConfirmSeconds;
    this.dirty = this.dirty || result.dirty;
    this.emitFeedbackBatch(result.feedback);
  }

  private startPrestigeOverdrive(): void {
    this.activePanel = null;
    const result = runPrestigeOverdriveCommand(this.save, this.progress(), Boolean(this.storm), Math.floor(Date.now() / 1000));
    this.dirty = this.dirty || result.dirty;
    this.emitFeedbackBatch(result.feedback);
  }

  private applyBounceStats(coreIds: readonly number[]): void {
    if (coreIds.length === 0) {
      return;
    }

    const countedBounces = coreIds
      .map((id) => this.cores.find((core) => core.id === id))
      .filter((core) => core?.countsAsProgress).length;
    this.save.stats.TotalChargeBounces += countedBounces;
    this.save.stats.DailyChargeBounces += countedBounces;
    this.save.stats.WeeklyChargeBounces += countedBounces;
    this.dirty = true;
  }

  private applyCollections(collectedCores: readonly { readonly coreId: number; readonly landingX: number }[]): void {
    const result = applyCollectedCoreRewards({
      save: this.save,
      cores: this.cores,
      collectedCores,
      effects: this.effects(),
      lastPayout: this.lastPayout,
      collectorEvents: {
        overdriveKind: this.overdriveView().active ? this.overdriveView().activeKind : null
      }
    });
    this.cores = result.cores;
    this.lastPayout = result.lastPayout;
    this.dirty = this.dirty || result.dirty;
    this.emitFeedbackBatch(result.feedback);
  }

  private claimLuckySpark(): void {
    const result = runClaimLuckySparkCommand(this.save, this.luckySpark, this.progress());
    this.dirty = this.dirty || result.dirty;
    this.emitFeedbackBatch(result.feedback);
  }

  private advanceTimedEvents(deltaSeconds: number): void {
    const result = advanceRuntimeTimedEvents({
      save: this.save,
      progress: this.progress(),
      luckySpark: this.luckySpark,
      deltaSeconds,
      stormActive: Boolean(this.storm),
      activePanel: this.activePanel,
      modalBlocked: this.save.crates.pendingResult !== null
    });
    this.dirty = this.dirty || result.dirty;
    this.emitFeedbackBatch(result.feedback);
  }

  private advanceAuto(deltaSeconds: number): void {
    const result = advanceAutoLaunch({
      save: this.save,
      cores: this.cores,
      effects: this.effects(),
      deltaSeconds,
      stormActive: Boolean(this.storm)
    });
    this.cores = result.cores;
    this.dirty = this.dirty || result.dirty;
    this.emitFeedbackBatch(result.feedback);
  }

  private advanceStorm(deltaSeconds: number): void {
    if (!this.storm) {
      return;
    }

    const result = advanceReactorStorm(this.save, this.storm, this.effects(), this.progress(), deltaSeconds);
    this.storm = result.state;
    this.dirty = this.dirty || result.dirty;
    this.emitFeedbackBatch(result.feedback);
  }

  private overdriveView() {
    return buildOverdriveView(this.save, this.progress(), Boolean(this.storm), Math.floor(Date.now() / 1000));
  }

  private advancePeriodStats(deltaSeconds: number): void {
    const nowUnixSeconds = Math.floor(Date.now() / 1000);
    this.dirty = syncDailyIgnition(this.save, nowUnixSeconds, this.progress().prestigeLevel) || this.dirty;
    this.dirty = consumeDailyIgnition(this.save, deltaSeconds) || this.dirty;
    this.save = advanceRuntimePeriodStats(this.save, deltaSeconds, nowUnixSeconds);
    this.dirty = true;
  }

  private effects() {
    return calculateUpgradeEffects(this.save.upgrades, this.progress());
  }

  private progress() {
    return lookupProfileProgress(this.save.stats);
  }

  private rebuildShells(): void {
    this.shells = rebuildShellsKeepingAngles(this.shells, this.effects());
  }

  private emitFeedback(event: FeedbackDraft): void {
    this.feedback = [
      ...this.feedback,
      createFeedbackEvent(event, this.feedbackId, this.elapsedSeconds)
    ];
    this.feedbackId += 1;
  }

  private emitFeedbackBatch(events: readonly FeedbackDraft[]): void {
    for (const event of events) {
      this.emitFeedback(event);
    }
  }

  private pruneFeedback(): void {
    this.feedback = pruneExpiredFeedback(this.feedback, this.elapsedSeconds);
  }

  private writeSave(storage: Storage | undefined): void {
    const nowUnixSeconds = Math.floor(Date.now() / 1000);
    this.save = {
      ...this.save,
      updatedAtUnixSeconds: nowUnixSeconds,
      lastSeenUnixSeconds: nowUnixSeconds
    };
    const result = writeOrbitForgeSave(storage, this.save);
    if (result.ok) {
      this.dirty = false;
      this.autosaveSeconds = 0;
    }
  }

  private publish(): void {
    const snapshot = this.snapshot();
    for (const subscriber of this.subscribers) {
      subscriber(snapshot);
    }
  }
}
